import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ErrorMessage, Formik } from "formik";
import * as Yup from "yup";
import visibility from "../../assets/icons/visibility.svg";
import visibilityOff from "../../assets/icons/visibility_off.svg";
import { connect, useDispatch, useSelector } from "react-redux";
import { withRouter, notification, gotProperty } from "../../utils/utility";
import PropTypes from "prop-types";
import isEmpty from "../../validation/is-empty";
import { loginUser } from "../../actions/authActions";
import Loader from "../../components/Loader/Loader";
import {
  getUserDashboardLink,
  getUserRoles,
  getUserBusinesses,
  shouldShowPortalSelectionAfterLogin,
} from "../../utils/authHelper";
import CryptoJS from "crypto-js";
import { USER_TYPE_PREFIX } from "../../utils/prefixes";
import { USER_TYPES } from "../../routes/types/private";
import { GET_ERRORS } from "../../actions/types";
import { buildSocialAuthUrl } from "../../utils/socialAuth";
import SocialAuthButtons from "../../components/SocialAuthButtons/SocialAuthButtons";
import AuthBrandPanel from "../../components/AuthBrandPanel/AuthBrandPanel";

const AuthCloseIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3.5 3.5l9 9M12.5 3.5l-9 9"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
  </svg>
);

const Login = (props) => {
  const dispatch = useDispatch();

  const [passwordVisibility, setPasswordVisibility] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const featuresLoaded = useSelector((state) => state.featureAccess.loaded);

  const Schema = Yup.object().shape({
    email: Yup.string().required("Email cannot be empty"),
    password: Yup.string().required("Password cannot be empty"),
  });

  useEffect(() => {
    if (!isEmpty(props.errors)) {
      setIsLoading(false);
      notification.error(props.errors);
      dispatch({
        type: GET_ERRORS,
        payload: null,
      });
    }
  }, [props.errors]);

  useEffect(() => {
    if (!props.auth.isAuthenticated) {
      return;
    }

    const userType = localStorage.userType;
    if (userType === USER_TYPES.admin && !featuresLoaded) {
      return;
    }

    setIsLoading(false);
    if (userType) {
      if (shouldShowPortalSelectionAfterLogin()) {
        localStorage.removeItem("userType");
        localStorage.removeItem("businessId");
        navigate("/admin/user-type");
      } else {
        navigate(getUserDashboardLink(userType));
      }
    } else {
      const userRoles = getUserRoles();
      const userBusinesses = getUserBusinesses();

      let businessIds = [];
      if (!isEmpty(userBusinesses)) {
        for (let userBusiness in userBusinesses) {
          businessIds.push(userBusinesses[userBusiness]);
        }
      }

      if (
        userRoles.length === 1 &&
        (businessIds.length === 0 || businessIds.length === 1) &&
        !shouldShowPortalSelectionAfterLogin()
      ) {
        localStorage.setItem(
          "userType",
          CryptoJS.SHA256(USER_TYPE_PREFIX + userRoles.pop())
        );
        if (
          localStorage.userType === USER_TYPES.admin &&
          businessIds.length === 1
        ) {
          localStorage.setItem("businessId", businessIds.pop());
        }

        if (localStorage.userType === USER_TYPES.admin && !featuresLoaded) {
          return;
        }

        navigate(getUserDashboardLink(localStorage.getItem("userType")));
      } else {
        navigate("/admin/user-type");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.auth.isAuthenticated, featuresLoaded]);

  const formikProps = {
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Schema,
    onSubmit: (values) => {
      const data = {
        email: values.email,
        password: values.password,
      };
      setIsLoading(true);
      props.loginUser(data, function (response) {
        if (
          gotProperty(response, "google_2fa_status") &&
          response?.google_2fa_status == "E"
        ) {
          if (!response.challenge_token) {
            setIsLoading(false);
            notification.error(
              "Unable to start two-factor verification. Please try again."
            );
            return;
          }
          setIsLoading(false);
          navigate(
            `/verify-pin/${encodeURIComponent(response.challenge_token)}`
          );
        }
      });
    },
  };

  const changePasswordVisibility = () => {
    setPasswordVisibility(!passwordVisibility);
  };

  const startSocialLogin = (provider) => {
    window.location.href = buildSocialAuthUrl(provider, "login");
  };

  return (
    <>
      <Loader isShowLoader={isLoading} />
      <Formik {...formikProps}>
        {(formik) => {
          const { values, handleChange, handleSubmit, handleBlur } = formik;
          return (
            <div className="signup-process auth-page">
              <AuthBrandPanel mode="login" />
              <div className="signup">
                <div className="cross-btn">
                  <Link to="/" className="auth-close" aria-label="Close">
                    <AuthCloseIcon />
                  </Link>
                </div>
                <div className="signup-form signup-form--centered">
                  <form onSubmit={handleSubmit}>
                    <div className="auth-form-header">
                      <p className="auth-form-header__eyebrow">Account login</p>
                      <h1>Welcome back</h1>
                      <p className="r-p">
                        Sign in to manage your business scheduling workspace.
                      </p>
                    </div>
                    <div className="form-group mb-0">
                      <label className="same-label" htmlFor="email">
                        Work email
                      </label>
                      <input
                        type="email"
                        className="form-control same-input"
                        name="email"
                        id="email"
                        placeholder="you@company.com"
                        autoComplete="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </div>
                    <ErrorMessage
                      name="email"
                      render={(error) => <span className="error">{error}</span>}
                    />
                    <div className="form-group mb-0">
                      <label className="same-label" htmlFor="password">
                        Password
                      </label>
                      <div className="password-group">
                        <input
                          type={passwordVisibility ? "text" : "password"}
                          className="form-control same-input"
                          name="password"
                          id="password"
                          placeholder="Enter your password"
                          autoComplete="current-password"
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {!passwordVisibility && (
                          <img
                            src={visibilityOff}
                            onClick={changePasswordVisibility}
                            alt=""
                          />
                        )}
                        {passwordVisibility && (
                          <img
                            src={visibility}
                            onClick={changePasswordVisibility}
                            alt=""
                          />
                        )}
                      </div>
                      <ErrorMessage
                        name="password"
                        render={(error) => (
                          <span className="error">{error}</span>
                        )}
                      />
                    </div>
                    <div className="more-opt">
                      <div className="form-group d-flex align-items-center w-auto m-0 p-0">
                        <input type="checkbox" name="remember" id="remember" />
                        <label className="same-label" htmlFor="remember">
                          Remember me
                        </label>
                      </div>
                      <Link to="/forgot-password">Forgot password?</Link>
                    </div>
                    <button type="submit" className="btn btn-app">
                      Sign in
                    </button>
                    <SocialAuthButtons
                      onGoogle={() => startSocialLogin("google")}
                      onMicrosoft={() => startSocialLogin("microsoft")}
                    />
                    <p className="login-here">
                      Don&apos;t have an account?{" "}
                      <Link to="/signup">Create one</Link>
                    </p>
                  </form>
                </div>
                <div className="signup-footer">
                  <p>© {new Date().getFullYear()} MeetOcto. All rights reserved.</p>
                </div>
              </div>
            </div>
          );
        }}
      </Formik>
    </>
  );
};

Login.propTypes = {
  loginUser: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  errors: state.errors,
});

export default connect(mapStateToProps, { loginUser })(withRouter(Login));
